import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Registration.module.css';
import { useDeleteRegistration, useRegistrationConfirmation } from './registrationHooks';
import ConfirmationModal from '../shared/ConfirmationModal';
import { CheckCircle2, Edit2, SmilePlus, Trash2 } from 'lucide-react';
import { formatDateLiteral } from '@iziping/common';

const ConfirmationRegistrationPage: React.FC = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data, isLoading, error } = useRegistrationConfirmation(uuid!);
    const deleteMutation = useDeleteRegistration();

    const [showConfirm, setShowConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = () => {
        setShowConfirm(true);
    };

    const confirmDelete = async () => {
        if (!data) return;

        setIsDeleting(true);
        try {
            await deleteMutation.mutateAsync({ id: data.registrationId });
            setShowConfirm(false);
            navigate(`/registration/${data?.tournamentId}`);
        } catch (err: any) {
            alert('Erreur lors de la suppression : ' + err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) return <p>{t('REGISTRATION_LOADING')}</p>;
    if (error) return <p className={styles.errorText}>Erreur</p>;

    return (
        data && (
            <div className={styles.pageWrapper}>
                <div className={styles.registrationContainer}>
                    <div className={styles.successIconWrapper}>
                        <CheckCircle2 size={48} />
                    </div>
                    <h2>{data.tournamentName}</h2>
                    <h2 className={styles.summaryTitle}>{t('REGISTRATION_CONFIRM_TITLE')}</h2>
                    <h3>{t('REGISTRATION_CONFIRM_EMAIL', { email: data.email })}</h3>

                    <div className={styles.summaryCard}>
                        <p>
                            <strong>
                                {data.firstname} {data.lastname}
                            </strong>
                        </p>

                        <div className={styles.heatsSection}>
                            <strong className={styles.summaryTitle}>{t('REGISTRATION_CONFIRM_HEATS')}</strong>
                            <div className={styles.badgeContainer}>
                                {data.heatsRegistered.map((heat, index) => (
                                    <span key={index} className={styles.badge}>
                                        {heat}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <h3>{t('REGISTRATION_RENDEZ_VOUS', { date: formatDateLiteral(data.date) })}</h3>

                    <div className={styles.statsCard}>
                        <p>
                            Total inscrits : <strong>{data.stats.nbRegistered}</strong>
                        </p>
                        <ul className={styles.statsList}>
                            {Object.entries(data.stats.registeredByHeats).map(([heat, count]) => (
                                <li key={heat}>
                                    <span className={styles.heatName}>{heat}</span>
                                    <span className={styles.heatCount}>{count}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={styles.actionsContainer}>
                        <button onClick={() => navigate(`/registration/${data.tournamentId}`)}>
                            <SmilePlus size={16} className={styles.btnIcon} />
                            {t('REGISTRATION_ADD_BUTTON')}
                        </button>

                        <button
                            onClick={() => navigate(`/registration/${data.tournamentId}/edit/${data.registrationId}`)}
                        >
                            <Edit2 size={16} className={styles.btnIcon} />
                            {t('REGISTRATION_MODIFY_BUTTON')}
                        </button>

                        <button className={styles.deleteButton} onClick={handleDelete} disabled={isDeleting}>
                            <Trash2 size={16} className={styles.btnIcon} />
                            {isDeleting ? t('REGISTRATION_DELETING_BUTTON') : t('REGISTRATION_DELETE_BUTTON')}
                        </button>
                    </div>

                    <ConfirmationModal
                        isOpen={showConfirm}
                        title={t('REGISTRATION_CONFIRM_DELETE_TITLE')}
                        message={t('REGISTRATION_CONFIRM_DELETE_MESSAGE')}
                        confirmLabel={t('REGISTRATION_CONFIRM_DELETE_BUTTON')}
                        cancelLabel={t('REGISTRATION_CONFIRM_CANCEL_BUTTON')}
                        onCancel={() => setShowConfirm(false)}
                        onConfirm={confirmDelete}
                    />
                </div>
            </div>
        )
    );
};

export default ConfirmationRegistrationPage;
